import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const EmailVerificationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage(t('emailVerification.noToken', 'No verification token provided'));
        return;
      }

      try {
        const response = await axios.post('/api/auth/verify-email', { token });
        
        if (response.data.success) {
          setVerificationStatus('success');
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setVerificationStatus('error');
          setErrorMessage(response.data.error || t('emailVerification.verificationFailed', 'Email verification failed'));
        }
      } catch (error: any) {
        console.error('Email verification error:', error);
        
        if (error.response?.status === 400) {
          const errorData = error.response.data;
          if (errorData.error?.includes('expired') || errorData.error?.includes('Expired')) {
            setVerificationStatus('expired');
          } else {
            setVerificationStatus('error');
            setErrorMessage(errorData.error || t('emailVerification.verificationFailed', 'Email verification failed'));
          }
        } else {
          setVerificationStatus('error');
          setErrorMessage(t('emailVerification.networkError', 'Network error. Please try again.'));
        }
      }
    };

    verifyEmail();
  }, [token, navigate, t]);

  const handleResendEmail = async () => {
    try {
      setVerificationStatus('loading');
      await axios.post('/api/auth/send-email-verification');
      setVerificationStatus('success');
      setErrorMessage('');
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error: any) {
      setVerificationStatus('error');
      setErrorMessage(t('emailVerification.resendFailed', 'Failed to resend verification email'));
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'loading':
        return (
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <h2 className="text-xl font-semibold text-gray-900 mt-4">
              {t('emailVerification.verifying', 'Verifying your email...')}
            </h2>
            <p className="text-gray-600 mt-2">
              {t('emailVerification.pleaseWait', 'Please wait while we verify your email address.')}
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('emailVerification.success.title', 'Email Verified Successfully!')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('emailVerification.success.message', 'Your email address has been verified. You can now use all features of the platform.')}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleGoToDashboard}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('emailVerification.goToDashboard', 'Go to Dashboard')}
              </button>
              <p className="text-sm text-gray-500">
                {t('emailVerification.redirecting', 'Redirecting automatically in 3 seconds...')}
              </p>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('emailVerification.expired.title', 'Verification Link Expired')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('emailVerification.expired.message', 'The verification link has expired. Please request a new verification email.')}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('emailVerification.resendEmail', 'Resend Verification Email')}
              </button>
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                {t('emailVerification.goToLogin', 'Go to Login')}
              </button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <XCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('emailVerification.error.title', 'Verification Failed')}
            </h2>
            <p className="text-gray-600 mb-4">
              {errorMessage || t('emailVerification.error.message', 'There was an error verifying your email address.')}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('emailVerification.resendEmail', 'Resend Verification Email')}
              </button>
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                {t('emailVerification.goToLogin', 'Go to Login')}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('emailVerification.pageTitle', 'Email Verification')} - tvia.ai</title>
        <meta name="description" content={t('emailVerification.pageDescription', 'Verify your email address to complete your account setup')} />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 sm:space-y-8">
          <div>
            <h1 className="text-center text-3xl font-bold text-gray-900 mb-2">
              tvia.ai
            </h1>
            <p className="text-center text-gray-600">
              {t('emailVerification.pageSubtitle', 'Email Verification')}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            {renderContent()}
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500">
              {t('emailVerification.needHelp', 'Need help?')}{' '}
              <a href="/contact" className="text-blue-600 hover:text-blue-500">
                {t('emailVerification.contactSupport', 'Contact Support')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailVerificationPage; 